import React from "react";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import { defaultSeo, organizationSchema } from "../lib/seoConfig";

/**
 * Enhanced SEO component for optimized metadata across pages
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  canonical,
  type,
  noindex,
  schema,
  imageAlt,
  locale,
  publishedAt,
  modifiedAt,
  category,
  tags,
  section,
  ogTitle,
  twitterTitle,
  ogDescription,
  twitterDescription,
}) => {
  // Values used in meta tags (document title vs social can differ)
  const metaTitle = title || defaultSeo.title;
  const metaDescription = description || defaultSeo.description;
  const socialDescription = ogDescription ?? metaDescription;
  const metaKeywords = keywords || defaultSeo.keywords;
  const metaImage = image || defaultSeo.image;
  const metaImageAlt = imageAlt || defaultSeo.imageAlt;
  const metaUrl = url || defaultSeo.url;
  const metaType = type || defaultSeo.type;
  const metaCanonical = canonical || metaUrl;
  const metaLocale = locale || defaultSeo.locale;
  const socialTitle = ogTitle ?? twitterTitle ?? metaTitle;
  const twitterCardDescription = twitterDescription ?? socialDescription;

  // Combine provided schema with organization schema (flatten if schema is already an array)
  const combinedSchema = (() => {
    const pieces = [];
    if (schema) {
      if (Array.isArray(schema)) {
        pieces.push(...schema);
      } else {
        pieces.push(schema);
      }
    }
    pieces.push(organizationSchema);
    return pieces;
  })();

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{metaTitle}</title>
      <meta name="title" content={metaTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}
      <link rel="canonical" href={metaCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={metaType} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:secure_url" content={metaImage} />
      <meta property="og:image:alt" content={metaImageAlt} />
      <meta property="og:locale" content={metaLocale} />
      <meta property="og:site_name" content={defaultSeo.siteName} />
      {publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {modifiedAt && (
        <meta property="article:modified_time" content={modifiedAt} />
      )}
      {section && <meta property="article:section" content={section} />}
      {tags &&
        tags.map((tag, i) => (
          <meta property="article:tag" content={tag} key={i} />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={metaUrl} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={metaImageAlt} />
      <meta name="twitter:creator" content={defaultSeo.twitterHandle} />
      <meta name="twitter:site" content={defaultSeo.twitterHandle} />

      {/* Additional Tags */}
      <meta name="author" content={defaultSeo.siteName} />
      <meta name="application-name" content={defaultSeo.siteName} />
      <meta name="apple-mobile-web-app-title" content={defaultSeo.siteName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="format-detection" content="telephone=no" />

      {/* Mobile and Performance */}
      <meta name="theme-color" content="#2C5F2D" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />

      {/* Content category for better categorization */}
      {category && <meta name="category" content={category} />}

      {/* Structured Data schema.org */}
      {combinedSchema.map((schemaItem, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(schemaItem)}
        </script>
      ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  url: PropTypes.string,
  canonical: PropTypes.string,
  type: PropTypes.string,
  noindex: PropTypes.bool,
  schema: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  locale: PropTypes.string,
  publishedAt: PropTypes.string,
  modifiedAt: PropTypes.string,
  category: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  section: PropTypes.string,
  ogTitle: PropTypes.string,
  twitterTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  twitterDescription: PropTypes.string,
};

export default SEO;
